import React, { useRef, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Select, MenuItem,
  IconButton, Tooltip, keyframes, Checkbox, LinearProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  TaskData, TaskStatus, TaskPriority, TaskCategory,
  STATUS_LABELS, PRIORITY_LABELS, CATEGORY_LABELS,
} from '../../types/task';

const celebrate = keyframes`
  0%   { transform: scale(1); }
  30%  { transform: scale(1.06); }
  60%  { transform: scale(0.97); }
  100% { transform: scale(1); }
`;

const taskTitleClampSx = {
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  wordBreak: 'break-word' as const,
  lineHeight: 1.4,
};

interface Props {
  tasks:             TaskData[];
  celebrateTaskId:   number | null;
  onEdit:            (task: TaskData) => void;
  onDelete:          (task: TaskData) => void;
  onStatusChange:    (id: number, status: TaskStatus) => void;
  onSubTaskToggle:   (taskId: number, subTaskId: string, checked: boolean) => void;
  selectedCategory?: TaskCategory;
}

const PRIORITY_BADGE_COLOR: Record<TaskPriority, string> = {
  [TaskPriority.HIGH]:   '#e53935',
  [TaskPriority.MEDIUM]: '#fb8c00',
  [TaskPriority.LOW]:    '#9e9e9e',
};

const CATEGORY_CHIP_COLOR: Record<TaskCategory, { bg: string; text: string }> = {
  [TaskCategory.ENTRY]:            { bg: '#e3f2fd', text: '#1565c0' },
  [TaskCategory.ES_SUBMISSION]:    { bg: '#fff3e0', text: '#e65100' },
  [TaskCategory.APTITUDE_TEST]:    { bg: '#f3e5f5', text: '#6a1b9a' },
  [TaskCategory.FIRST_INTERVIEW]:  { bg: '#e8f5e9', text: '#2e7d32' },
  [TaskCategory.SECOND_INTERVIEW]: { bg: '#e0f7fa', text: '#006064' },
  [TaskCategory.FINAL_INTERVIEW]:  { bg: '#fce4ec', text: '#880e4f' },
  [TaskCategory.OFFER]:            { bg: '#fff8e1', text: '#f57f17' },
};

const COLUMN_CONFIG: { category: TaskCategory; label: string; color: string }[] = [
  { category: TaskCategory.ENTRY,            label: 'エントリー', color: '#1565c0' },
  { category: TaskCategory.ES_SUBMISSION,    label: 'ES提出',     color: '#e65100' },
  { category: TaskCategory.APTITUDE_TEST,    label: '適性検査',   color: '#6a1b9a' },
  { category: TaskCategory.FIRST_INTERVIEW,  label: '一次面接',   color: '#2e7d32' },
  { category: TaskCategory.SECOND_INTERVIEW, label: '二次面接',   color: '#006064' },
  { category: TaskCategory.FINAL_INTERVIEW,  label: '最終面接',   color: '#880e4f' },
  { category: TaskCategory.OFFER,            label: '内定',       color: '#f57f17' },
];

function getRelativeDays(dueDate: string): { label: string; color: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diff === 0) return { label: '今日',                  color: '#f44336' };
  if (diff === 1) return { label: '明日',                  color: '#ff7043' };
  if (diff < 0)  return { label: `${Math.abs(diff)}日前`, color: '#9e9e9e' };
  if (diff <= 3) return { label: `${diff}日後`,            color: '#ffa726' };
  return           { label: `${diff}日後`,                 color: '#1976d2' };
}

function formatDueDate(dueDate: string): string {
  const d = new Date(dueDate);
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
  return `${d.getMonth() + 1}月${d.getDate()}日（${weekDays[d.getDay()]}）`;
}

export const TaskKanbanView: React.FC<Props> = ({
  tasks, celebrateTaskId, onEdit, onDelete, onStatusChange, onSubTaskToggle, selectedCategory,
}) => {
  const columnRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (selectedCategory) {
      const el = columnRefs.current[selectedCategory];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    } else {
      const firstCategory = COLUMN_CONFIG[0].category;
      const el = columnRefs.current[firstCategory];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }, [selectedCategory]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'start',
        pb: 1,
      }}
    >
      {COLUMN_CONFIG.map(({ category, label, color }) => {
        const columnTasks = tasks.filter((t) => t.category === category);

        return (
          <Box
            key={category}
            ref={(el: HTMLDivElement | null) => { columnRefs.current[category] = el; }}
            sx={{
              width: 260,
              minWidth: 260,
              maxWidth: 260,
              flexShrink: 0,
              minHeight: 0,
            }}
          >
            {/* カラムヘッダー */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
              <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
              <Chip
                label={columnTasks.length}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>

            {/* カラム本体 */}
            <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 1.5, minHeight: 120 }}>
              {columnTasks.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{ textAlign: 'center', py: 3 }}
                >
                  なし
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {columnTasks.map((task) => {
                    const isCelebrating  = celebrateTaskId === task.id;
                    const isCompleted    = task.status === TaskStatus.COMPLETED;
                    const subTasks       = task.sub_tasks ?? [];
                    const completedCount = subTasks.filter((s) => s.is_completed).length;

                    return (
                      <Card
                        key={task.id}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          minWidth: 0,
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                          opacity:   isCompleted ? 0.72 : 1,
                          animation: isCelebrating ? `${celebrate} 0.6s ease` : 'none',
                          transition: 'box-shadow 0.2s',
                          '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.12)' },
                        }}
                      >
                        <CardContent sx={{ pb: '12px !important', px: 2, pt: 2, overflow: 'hidden' }}>

                          {/* 行1: 会社名（左）+ カテゴリバッジ（右） */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              {task.company_name ?? ''}
                            </Typography>
                            <Box
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: CATEGORY_CHIP_COLOR[task.category]?.bg ?? '#f5f5f5',
                                color:   CATEGORY_CHIP_COLOR[task.category]?.text ?? '#555',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {CATEGORY_LABELS[task.category]}
                            </Box>
                          </Box>

                          {/* 行2: 優先度バッジ + タイトル（長文は最大2行で省略） */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.25 }}>
                            <Box
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.75,
                                bgcolor: PRIORITY_BADGE_COLOR[task.priority],
                                color: 'white',
                                flexShrink: 0,
                                letterSpacing: '0.5px',
                              }}
                            >
                              {PRIORITY_LABELS[task.priority]}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <Tooltip title={task.title}>
                                <Typography
                                  variant="subtitle1"
                                  component="span"
                                  fontWeight={700}
                                  sx={{
                                    ...taskTitleClampSx,
                                    textDecoration: isCompleted ? 'line-through' : 'none',
                                    color: isCompleted ? 'text.disabled' : 'text.primary',
                                  }}
                                >
                                  {task.title}
                                </Typography>
                              </Tooltip>
                              {isCelebrating && (
                                <EmojiEventsIcon sx={{ flexShrink: 0, color: 'warning.main', fontSize: '1rem', mt: '2px' }} />
                              )}
                            </Box>
                          </Box>

                          {/* 行3: 締切日 */}
                          {task.due_date && (() => {
                            const rel = getRelativeDays(task.due_date);
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {formatDueDate(task.due_date)}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: rel.color }}>
                                  {rel.label}
                                </Typography>
                              </Box>
                            );
                          })()}

                          {/* 行4: 時刻 */}
                          {task.scheduled_time_start && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                              <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {task.scheduled_time_start}
                              </Typography>
                            </Box>
                          )}

                          {/* 行5: 場所 */}
                          {task.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                              <LocationOnIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {task.location}
                              </Typography>
                            </Box>
                          )}

                          {/* 行6: 説明（メモボックス） */}
                          {task.description && (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.75,
                                alignItems: 'flex-start',
                                bgcolor: 'grey.50',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1.5,
                                px: 1.25,
                                py: 1,
                                mb: 1.25,
                                mt: 0.5,
                              }}
                            >
                              <DescriptionIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mt: '2px', flexShrink: 0 }} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.5,
                                }}
                              >
                                {task.description}
                              </Typography>
                            </Box>
                          )}

                          {/* 行7: サブタスクチェックリスト */}
                          {subTasks.length > 0 && (
                            <Box sx={{ mb: 1.25, mt: task.description ? 0 : 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                  タスク
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {completedCount}/{subTasks.length} 完了
                                </Typography>
                              </Box>
                              {subTasks.length > 1 && (
                                <LinearProgress
                                  variant="determinate"
                                  value={(completedCount / subTasks.length) * 100}
                                  sx={{ mb: 0.75, height: 3, borderRadius: 2 }}
                                />
                              )}
                              {subTasks.map((item) => (
                                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Checkbox
                                    size="small"
                                    checked={item.is_completed}
                                    onChange={(e) => onSubTaskToggle(task.id, item.id, e.target.checked)}
                                    sx={{ p: 0.25 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    color={item.is_completed ? 'text.disabled' : 'text.primary'}
                                    sx={{ textDecoration: item.is_completed ? 'line-through' : 'none', fontSize: '0.8rem' }}
                                  >
                                    {item.title}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* 行8: ステータス変更（左）+ 編集・削除（右） */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Select
                              size="small"
                              value={task.status}
                              onChange={(e) => onStatusChange(task.id, e.target.value as TaskStatus)}
                              sx={{ fontSize: '0.78rem', height: 30, minWidth: 100 }}
                            >
                              {Object.values(TaskStatus).map((s) => (
                                <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>
                                  {STATUS_LABELS[s]}
                                </MenuItem>
                              ))}
                            </Select>

                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="編集">
                                <IconButton size="small" onClick={() => onEdit(task)} sx={{ color: 'text.secondary' }}>
                                  <EditIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="削除">
                                <IconButton size="small" color="error" onClick={() => onDelete(task)}>
                                  <DeleteIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
